/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import {
  PLACEHOLDER_DASH,
  TROY_SUPPORT_LANG,
} from "../constants/misc"

interface LocaleInfo {
  capital: string
  capitalTimezone: string
  countryCode: string
  countryName: string
  currencyCode: string
  currencySymbol: string
  decimalSymbol: string
  decimals: number
  exponentUnit: number
  thousandSymbol: string
}

const IDLocale: LocaleInfo = {
  capital: "Jakarta",
  capitalTimezone: "Asia/Jakarta",
  countryCode: "ID",
  countryName: "Indonesia",
  currencyCode: "IDR",
  currencySymbol: "Rp",
  decimalSymbol: ",",
  decimals: 0,
  exponentUnit: 2,
  thousandSymbol: ".",
}

const MYLocale: LocaleInfo = {
  capital: "Kuala Lumpur",
  capitalTimezone: "Asia/Kuala_Lumpur",
  countryCode: "MY",
  countryName: "Malaysia",
  currencyCode: "MYR",
  currencySymbol: "RM",
  decimalSymbol: ".",
  decimals: 2,
  exponentUnit: 2,
  thousandSymbol: ",",
}

const THLocale: LocaleInfo = {
  capital: "Bangkok",
  capitalTimezone: "Asia/Bangkok",
  countryCode: "TH",
  countryName: "Thailand",
  currencyCode: "THB",
  currencySymbol: "฿",
  decimalSymbol: ".",
  decimals: 0,
  exponentUnit: 2,
  thousandSymbol: ",",
}

const VNLocale: LocaleInfo = {
  capital: "Hanoi",
  capitalTimezone: "Asia/Ho_Chi_Minh",
  countryCode: "VN",
  countryName: "Vietnam",
  currencyCode: "VND",
  currencySymbol: "₫",
  decimalSymbol: ",",
  decimals: 0,
  exponentUnit: 0,
  thousandSymbol: ".",
}

const PHLocale: LocaleInfo = {
  capital: "Manila",
  capitalTimezone: "Asia/Manila",
  countryCode: "PH",
  countryName: "Philippines",
  currencyCode: "PHP",
  currencySymbol: "₱",
  decimalSymbol: ".",
  decimals: 2,
  exponentUnit: 2,
  thousandSymbol: ",",
}

const SGLocale: LocaleInfo = {
  capital: "Singapore",
  capitalTimezone: "Asia/Singapore",
  countryCode: "SG",
  countryName: "Singapore",
  currencyCode: "SGD",
  currencySymbol: "S$",
  decimalSymbol: ".",
  decimals: 2,
  exponentUnit: 2,
  thousandSymbol: ",",
}

const MMLocale: LocaleInfo = {
  capital: "Naypyidaw",
  capitalTimezone: "Asia/Yangon",
  countryCode: "MM",
  countryName: "Myanmar",
  currencyCode: "MMK",
  currencySymbol: "KS",
  decimalSymbol: ".",
  decimals: 0,
  exponentUnit: 2,
  thousandSymbol: ",",
}

const KHLocale: LocaleInfo = {
  capital: "Phnom Penh",
  capitalTimezone: "Asia/Phnom_Penh",
  countryCode: "KH",
  countryName: "Cambodia",
  currencyCode: "KHR",
  currencySymbol: "៛",
  decimalSymbol: ".",
  decimals: 0,
  exponentUnit: 2,
  thousandSymbol: ",",
}

export const TWLocale: LocaleInfo = {
  capital: "Taipei",
  capitalTimezone: "Asia/Taipei",
  countryCode: "TW",
  countryName: "Taiwan",
  currencyCode: "TWD",
  currencySymbol: "NT$",
  decimalSymbol: ".",
  decimals: 2,
  exponentUnit: 2,
  thousandSymbol: ",",
}

export const getLocaleInfo = (
  currency: string,
): LocaleInfo => {
  switch (currency.toUpperCase()) {
    case TWLocale.currencyCode:
      return TWLocale
    case VNLocale.currencyCode:
      return VNLocale
    case IDLocale.currencyCode:
      return IDLocale
    case MYLocale.currencyCode:
      return MYLocale
    case PHLocale.currencyCode:
      return PHLocale
    case THLocale.currencyCode:
      return THLocale
    case MMLocale.currencyCode:
      return MMLocale
    case KHLocale.currencyCode:
      return KHLocale
    default:
      return SGLocale
  }
}

export const getLocaleInfoByCountryName = (
  countryName: string,
): LocaleInfo => {
  switch (countryName) {
    case VNLocale.countryName:
      return VNLocale
    case IDLocale.countryName:
      return IDLocale
    case MYLocale.countryName:
      return MYLocale
    case PHLocale.countryName:
      return PHLocale
    case THLocale.countryName:
      return THLocale
    case MMLocale.countryName:
      return MMLocale
    case TWLocale.countryName:
      return TWLocale
    default:
      return SGLocale
  }
}

export const getCurrencyCodeByCountryCode = (
  countryCode: string,
): string => {
  switch (countryCode) {
    case TWLocale.countryCode:
      return TWLocale.currencyCode
    case VNLocale.countryCode:
      return VNLocale.currencyCode
    case IDLocale.countryCode:
      return IDLocale.currencyCode
    case MYLocale.countryCode:
      return MYLocale.currencyCode
    case PHLocale.countryCode:
      return PHLocale.currencyCode
    case THLocale.countryCode:
      return THLocale.currencyCode
    case MMLocale.countryCode:
      return MMLocale.currencyCode
    case KHLocale.countryCode:
      return KHLocale.currencyCode
    default:
      return SGLocale.currencyCode
  }
}

// Maps display currency symbols to their ISO currency codes.
export const CURRENCY_SYMBOL_TO_CODE: Record<string, string> = {
  Rp: "IDR",
  RM: "MYR",
  "฿": "THB",
  "₫": "VND",
  "₱": "PHP",
  S$: "SGD",
  KS: "MMK",
  "៛": "KHR",
  "NT$": "TWD",
}

const doFormatCurrency = (
  amount: number,
  decimalCount = 2,
  decimal = ".",
  thousands = ",",
): string => {
  try {
    if (!Number.isFinite(amount)) {
      throw Error(`invalid amount:${amount}`)
    }
    let normalizedDecimalCount = Math.abs(
      decimalCount,
    )
    normalizedDecimalCount = Number.isNaN(
      normalizedDecimalCount,
    )
      ? 2
      : normalizedDecimalCount
    const negativeSign = amount < 0 ? "-" : ""
    const i = Math.abs(amount)
      .toFixed(normalizedDecimalCount)
      .toString()
    const [integerPart, decimalPart] = i.split(
      ".",
    )
    const j =
      integerPart.length > 3
        ? integerPart.length % 3
        : 0
    return (
      negativeSign +
      (j
        ? integerPart.substr(0, j) + thousands
        : "") +
      integerPart
        .substr(j)
        .replace(
          /(\d{3})(?=\d)/g,
          `$1${thousands}`,
        ) +
      (decimalPart ? decimal + decimalPart : "")
    )
  } catch (e) {
    return ""
  }
}

const baseCurrencyFormatter = (
  amount: number,
  currencyCode: string,
  withCurrencyCode?: boolean,
): string => {
  const localeInfo = getLocaleInfo(currencyCode)
  let result = doFormatCurrency(
    amount / 10 ** localeInfo.exponentUnit,
    localeInfo.decimals,
    localeInfo.decimalSymbol,
    localeInfo.thousandSymbol,
  )
  if (withCurrencyCode) {
    let sign = ""
    if (result.length === 0) {
      return `${currencyCode}`
    }
    if (result[0] === "-") {
      sign = "-"
      result = result.slice(1)
    }
    return `${currencyCode} ${sign}${result}`
  }
  return result
}

export const currencyWithCodeFormatter = (
  amount: number,
  currencyCode: string,
  showPlaceholderWhenZero: boolean = false,
  placeholder: string = PLACEHOLDER_DASH,
): string =>
  showPlaceholderWhenZero && amount === 0
    ? placeholder
    : baseCurrencyFormatter(
        amount,
        currencyCode,
        true,
      )

export const currencyWithoutCodeFormatter = (
  amount: number,
  currencyCode: string,
  showPlaceholderWhenZero: boolean = false,
  placeholder: string = PLACEHOLDER_DASH,
): string =>
  showPlaceholderWhenZero && amount === 0
    ? placeholder
    : baseCurrencyFormatter(
        amount,
        currencyCode,
        false,
      )

export const isVN = (
  currencyCode: string,
): boolean =>
  currencyCode.toUpperCase() ===
  VNLocale.currencyCode

export const isSG = (
  currencyCode: string,
): boolean =>
  currencyCode.toUpperCase() ===
  SGLocale.currencyCode

export const isID = (
  currencyCode: string,
): boolean =>
  currencyCode.toUpperCase() ===
  IDLocale.currencyCode

export const getCapitalTimezoneByCountryName = (
  countryName: string,
): string => {
  return getLocaleInfoByCountryName(countryName)
    .capitalTimezone
}

export const getCapitalByCountryName = (
  countryName: string,
): string => {
  return getLocaleInfoByCountryName(countryName)
    .capital
}

function getLanguageStringFromNavigatorObject(
  nav: Navigator & { userLanguage?: string },
): string {
  let browserLanguage = null

  if (nav.languages && nav.languages.length > 0) {
    browserLanguage = nav.languages[0].split(
      "-",
    )[0]
  } else if (nav.userLanguage) {
    // IE 10 only supports window.navigator.userLanguage
    browserLanguage = nav.userLanguage.split(
      "-",
    )[0]
  } else if (nav.language) {
    // Safari only supports window.navigator.language
    browserLanguage = nav.language.split("-")[0]
  }

  return browserLanguage || ""
}

export function getLocaleFromBrowserLanguage(opts: {
  defaultLocale: string
  availableLocales: string[]
  navigator: Navigator
}) {
  let locale = opts.defaultLocale || ""
  const browserLanguage = getLanguageStringFromNavigatorObject(
    opts.navigator || window.navigator,
  )
  const { availableLocales } = opts

  if (
    availableLocales &&
    availableLocales.length &&
    availableLocales.indexOf(browserLanguage) > -1
  ) {
    locale = browserLanguage
  }

  return locale
}

export const normalizeTroyLangLocale = (
  lang: string,
): string => {
  const langLower = lang.toLowerCase()
  if (
    TROY_SUPPORT_LANG.indexOf(langLower) === -1
  ) {
    return "en"
  }
  return langLower
}
